import React, { useState, useEffect, useRef, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useHistory, useLocation } from 'react-router-dom'
import { FilterButtonContext } from '../../provider/FilterButtonProvider/lib/FilterButtonContext'

import isEmpty from 'lodash/isEmpty'
import usePageLoaded from '../../hooks/usePageLoaded'
import { useInitOfSavingClearPath, useInitOfSavingSearchPath } from '../../hooks/useBackButton'
import useProtectImages from '../../hooks/useProtectImages'

import TagsFilters from './materials/tagsFilters'
import Grid from '../../components/grid/grid'
import Title from '../../components/ui/title/title'
import ContributionPreview from '../../components/contribution/contributionPreview'
import PaginationLoader from '../../components/ui/paginationLoader/PaginationLoader'
import PaginationAndEndlessScrollWrapper from '../index/PaginationAndEndlessScrollWrapper'
import MaterialPreview from '../../components/material/materialPreview'
import {
    PaginationSearchContributionsLink,
    PaginationSearchLink
} from '../../components/ui/paginationLink/PaginationLink'
import { Helmet } from 'react-helmet'
import { META } from '../../constants/meta'
import { getLangFromPath } from '../../i18next'
import { ShapedButton } from '../../components/ui/shapedButton/ShapedButton'
import ButtonLink from '../../components/ui/buttonLink'

import Pagination from 'rsuite/Pagination'

import { ContributionType } from '../../store/contributions/contributionsTypings'
import { RootState } from '../../store/rootReducer'
import { MaterialsType } from '../../store/tags/tagsTyping'

import {
    clearSearchList,
    fetchSearchResults,
    fetchTagsList,
    setCurrentSlug,
    setCurrentTag,
    setDisableLoading
} from '../../store/tags/tagsSlice'
import { createLocalizedPath, getPlural } from '../../utils/localizationHelpers'
import paths, { pureUrls } from '../../paths'
import { changeAmountOfPages, changeDefaultPageScrolledPagesAmountAndPosition } from '../../store/common/commonSlice'
import { getTag } from './../../api/tags'
import './tags.sass'
import {
    createContributionLink,
    createPageDescription,
    createPageTitle,
    createTagLink,
    getSlugFromPath
} from './TagsUtils'

const Search = (): JSX.Element => {
    usePageLoaded()
    useInitOfSavingSearchPath()

    const [t] = useTranslation()
    const dispatch = useDispatch()
    const history = useHistory()
    const location = useLocation()

    const contributions = useSelector<RootState, ContributionType[]>((state) => state.tags.contributions.list)
    const totalContributions = useSelector<RootState, number>((state) => state.tags.contributions.total)
    const materials = useSelector<RootState, MaterialsType[]>((state) => state.tags.materials.list)
    const totalMaterials = useSelector<RootState, number>((state) => state.tags.materials.total)
    const status = useSelector<RootState, string>((state) => state.tags.status)
    const currentTag = useSelector<RootState, string>((state) => state.tags.currentTag)
    const currentSlug = useSelector<RootState, string>((state) => state.tags.currentSlug)
    const disableLoading = useSelector<RootState, boolean>((state) => state.tags.disableLoading)
    const isAgeVerified = useSelector<RootState, boolean>((state) => state.common.isAgeVerified)

    useProtectImages(materials)

    const [isDescriptionFull, setIsDescriptionFull] = useState(false)
    const [isRenderMaterials, setIsRenderMaterials] = useState(!location.pathname.split('/').includes('contributions'))
    const filters = ['', 'photo', 'video']
    const [chosenFilter, setChosenFilter] = useState(filters[0])
    const lastElement = useRef(null)

    const setCurrentTagInStore = () => {
        const slug = getSlugFromPath(location.pathname + location.search)
        let tag
        if (!slug) {
            dispatch(setCurrentTag(''))
            return ''
        }

        getTag({ slug })
            .then((answer) => {
                if (currentTag !== answer.tag.text) dispatch(setCurrentTag(answer.tag.text))
                tag = answer.tag.text
            })
            .catch(() => {
                if (currentTag !== slug) dispatch(setCurrentTag(slug))
                tag = slug
            })
        return tag
    }

    const { setFilterButton } = useContext(FilterButtonContext)
    const isPageScrolled = useSelector<RootState, boolean>((state) => state.common.isPageScrolled)

    const getCurrentPage = () => {
        if (location.search) return Number(new URLSearchParams(location.search).get('p'))
        return undefined
    }
    const [currentPage, setCurrentPage] = useState(+getCurrentPage() || 1)

    const clearStore = () => {
        dispatch(clearSearchList())
        if (!getCurrentPage()) {
            setCurrentPage(1)
            dispatch(changeAmountOfPages({ page: 1 }))
        }
    }

    const currentRequestHeaders = () => {
        return { query: currentTag, p: currentPage, type: chosenFilter }
    }

    useEffect(() => {
        setCurrentTagInStore()
        if (getSlugFromPath(location.pathname + location.search) === currentSlug) {
            const currentFilter = location.pathname.includes('/photo/')
                ? 'photo'
                : location.pathname.includes('/video/')
                ? 'video'
                : ''

            const isQuery = location.search.includes('query')

            setChosenFilter(currentFilter)
            if (!disableLoading) {
                clearStore()
                dispatch(fetchSearchResults({ query: isQuery ? currentSlug : currentTag, type: currentFilter, p: 1 }))
            }
            {
                dispatch(setDisableLoading(false))
            }
        }
    }, [location.pathname, location.search])

    useEffect(() => {
        dispatch(setCurrentSlug(getSlugFromPath(location.pathname + location.search)))
        setCurrentTagInStore()
        return () => {
            dispatch(setDisableLoading(true))
        }
    }, [])

    return (
        <PaginationAndEndlessScrollWrapper
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            list={!isRenderMaterials ? contributions : materials}
            sourcePage={'tagsScrolling'}
            fetchList={fetchSearchResults(currentRequestHeaders())}
            clearList={clearSearchList()}
            amount_of_items_on_page={52}
            lastElement={lastElement}
            loading={status === 'loading'}
            total={!isRenderMaterials ? +totalContributions : +totalMaterials}
            isNecessaryToDisableFirstLoading={true}
        >
            <div className="pageContent" style={{ margin: '0 auto', maxWidth: '1360px', padding: '90px 27px' }}>
                <Helmet>
                    <title>{createPageTitle(location.pathname, currentTag)}</title>
                    <meta name="description" content={createPageDescription(location.pathname, currentTag)} />
                    <link
                        rel="canonical"
                        href={process.env.CLIENT_PUBLIC_PATH.slice(0, -1) + location.pathname + location.search}
                    />
                </Helmet>
                <Title text={t('Search photo and video by tags')} />
                {isDescriptionFull ? (
                    <div
                        onClick={() => {
                            setIsDescriptionFull(!isDescriptionFull)
                        }}
                    >
                        {t('Full description')}
                        <span className="textButton">{t('Show less')}</span>
                    </div>
                ) : (
                    <div
                        onClick={() => {
                            setIsDescriptionFull(!isDescriptionFull)
                        }}
                    >
                        {t('First line of description')}
                        <span className="textButton">{t('Show more')}</span>
                    </div>
                )}

                <TagsFilters />
                <div className="searchInfo">
                    <div>
                        <div data-testid={'searchInfo'}>{!isRenderMaterials ? totalContributions : totalMaterials}</div>
                        <div>
                            {t('Search result', {
                                postProcess: 'interval',
                                count: getPlural(
                                    !isRenderMaterials ? totalContributions : totalMaterials,
                                    getLangFromPath(location.pathname)
                                )
                            })}
                        </div>
                    </div>
                    <div>
                        <ButtonLink
                            // small
                            label={'Materials'}
                            secondary={!isRenderMaterials}
                            onClick={() => {
                                setIsRenderMaterials(true)
                                setCurrentPage(1)
                                dispatch(changeDefaultPageScrolledPagesAmountAndPosition())
                            }}
                            linkUrl={createLocalizedPath(createTagLink(location.pathname, currentSlug))}
                        />
                        <ButtonLink
                            // small
                            label={'Contributions'}
                            secondary={isRenderMaterials}
                            onClick={() => {
                                setIsRenderMaterials(false)
                                setCurrentPage(1)
                                dispatch(changeDefaultPageScrolledPagesAmountAndPosition())
                            }}
                            linkUrl={createLocalizedPath(createContributionLink(location.pathname, currentSlug))}
                        />
                        {!isPageScrolled && <ShapedButton onclick={() => setFilterButton(true)} />}
                    </div>
                </div>
                <Grid
                    items={
                        !isRenderMaterials
                            ? contributions.map((contribution: ContributionType, index) => (
                                  <ContributionPreview
                                      contribution={contribution}
                                      key={contribution.id}
                                      fromTags
                                      index={index}
                                      tag={currentTag}
                                      currentPage={getCurrentPage() ? currentPage - 1 : 0}
                                      isAgeVerified={isAgeVerified}
                                  />
                              ))
                            : materials.map((material: MaterialsType) => (
                                  <MaterialPreview
                                      material={material}
                                      key={material.id}
                                      isAgeVerified={isAgeVerified}
                                  />
                              ))
                    }
                />
                <div className="paginationWrapper">
                    {status !== 'loading' ? (
                        <Pagination
                            ref={lastElement}
                            limit={52}
                            limitOptions={[10]}
                            maxButtons={5}
                            activePage={currentPage}
                            linkAs={isRenderMaterials ? PaginationSearchLink : PaginationSearchContributionsLink}
                            onChangePage={(page: number) => {
                                history.push({
                                    pathname: isRenderMaterials
                                        ? createLocalizedPath(pureUrls.search)
                                        : createLocalizedPath(pureUrls.searchContributions),
                                    search: `?query=${currentTag}&p=${page}`
                                })
                                setCurrentPage(page)
                            }}
                            total={!isRenderMaterials ? +totalContributions : +totalMaterials}
                        />
                    ) : (
                        <PaginationLoader />
                    )}
                </div>
            </div>
        </PaginationAndEndlessScrollWrapper>
    )
}

// @ts-ignore
Search.getInitialProps = async ({ store, match, req }) => {
    const {
        tags: { materials, contributions }
    } = store.getState()

    let query = match.url.split('/').pop()

    const typeFromUrl = () => {
        if (match.url.includes('/photo/')) return 'photo'
        if (match.url.includes('/video/')) return 'video'
        return ''
    }

    if (query === 'tags' || query === ':tag?' || query === 'contribution') {
        query = ''
    }

    if (req) {
        let tagFromHtml = req.query.query
        const page = req.query.p

        if (!tagFromHtml) {
            tagFromHtml = query
        }

        if (isEmpty(materials.list) && isEmpty(contributions.list)) {
            await getTag({ slug: tagFromHtml })
                .then(async (answer) => {
                    await store.dispatch(fetchSearchResults({ p: page, query: answer.tag.text, type: typeFromUrl() }))
                })

                .catch(
                    async () =>
                        await store.dispatch(fetchSearchResults({ p: 1, query: tagFromHtml, type: typeFromUrl() }))
                )
        }
        getTag({ slug: tagFromHtml })
            .then((answer) => {
                store.dispatch(setCurrentTag(answer.tag.text))
            })
            .catch(() => {
                store.dispatch(setCurrentTag(tagFromHtml))
            })
    }
    await store.dispatch(fetchTagsList({ order: 'random', p: 1 }))
}
export default Search
